import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, ExternalLink } from 'lucide-react';
import './VideoViewer.css';

export default function VideoViewer() {
    const [searchParams] = useSearchParams();
    const fullVideoUrl = searchParams.get('url'); // Full storage URL
    const productName = searchParams.get('product');
    
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Debug logging
    useEffect(() => {
        console.log('[VideoViewer] Video URL:', fullVideoUrl);
        console.log('[VideoViewer] Product name:', productName);
    }, [fullVideoUrl, productName]);

    useEffect(() => {
        if (!fullVideoUrl) {
            setError('No video URL specified');
            setIsLoading(false);
        }
    }, [fullVideoUrl]);

    const handleDownload = () => {
        if (fullVideoUrl) {
            const filename = fullVideoUrl.split('/').pop() || 'product-video.webm';
            const a = document.createElement('a');
            a.href = fullVideoUrl;
            a.download = filename;
            a.click();
        }
    };

    const handleVideoLoad = () => {
        setIsLoading(false);
    };

    const handleVideoError = () => {
        setError('Failed to load video. The video may have been deleted.');
        setIsLoading(false);
    };

    if (error) {
        return (
            <div className="video-viewer-container">
                <div className="video-viewer-header">
                    <img src="/images/logo.png" alt="AddNectar" className="viewer-logo" />
                    <h1>Video Not Found</h1>
                </div>
                <div className="video-viewer-error">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="video-viewer-container">
            {/* Header with branding */}
            <div className="video-viewer-header">
                <div className="viewer-logo-container">
                    <h1 className="viewer-brand">Visual Tools</h1>
                    <span className="viewer-tagline">by AddNectar</span>
                </div>
                {productName && <h2 className="viewer-product-name">{productName}</h2>}
            </div>

            {/* Video Player */}
            <div className="video-player-wrapper">
                {isLoading && (
                    <div className="video-loading">
                        <div className="spinner"></div>
                        <p>Loading your video...</p>
                    </div>
                )}
                
                {fullVideoUrl && (
                    <video
                        className="video-player"
                        controls
                        autoPlay
                        loop
                        onLoadedData={handleVideoLoad}
                        onError={handleVideoError}
                        style={{ display: isLoading ? 'none' : 'block' }}
                    >
                        <source src={fullVideoUrl} type="video/webm" />
                        Your browser does not support the video tag.
                    </video>
                )}
            </div>

            {/* Action Buttons */}
            <div className="video-actions">
                <button className="viewer-btn viewer-btn-primary" onClick={handleDownload}>
                    <Download size={20} />
                    Download Video
                </button>
                <a 
                    href={fullVideoUrl || '#'} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="viewer-btn viewer-btn-secondary"
                >
                    <ExternalLink size={20} />
                    Open in New Tab
                </a>
            </div>

            {/* Info Section */}
            <div className="video-info">
                <h3>🎬 Your 360° Product Video</h3>
                <p>This video showcases your customized product design in a full 360° rotation.</p>
                <ul className="video-features">
                    <li>✓ High-quality WebM format</li>
                    <li>✓ Optimized for web and social media</li>
                    <li>✓ Download and share anywhere</li>
                </ul>
            </div>

            {/* Footer */}
            <footer className="video-viewer-footer">
                <p>Created with <span className="heart">❤️</span> using <strong>AddNectar Visual Tools</strong></p>
                <p className="footer-links">
                    <a href="https://addnectar.com" target="_blank" rel="noopener noreferrer">Visit AddNectar</a>
                    <span>•</span>
                    <a href="/" >Back to Configurator</a>
                </p>
            </footer>
        </div>
    );
}
